import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, Loader2, Network, Plus, Search } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Skeleton } from '@/components/ui/skeleton'
import { Icon } from '@/components/ui/icon'
import { AuthService } from '@/services/core/auth'
import { SearchApi } from '@/services/core/searchApi'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useModule } from '@/contexts/ModuleContext'
import { useNetwork } from '@/contexts/NetworkContext'
import { useSavedQueries, type SavedQuery } from '@/hooks/useSavedQueries'
import { fuzzyScore } from '@/lib/fuzzy'
import { useCommandActions } from './actions'
import { SEARCH_MODES } from './searchModes'
import {
  ACTION_GROUPS,
  PICKER_MODES,
  type CommandAction,
  type PaletteMode,
  type PickerMode,
} from './types'

/**
 * The list scrolls at this many rows, like the VS Code palette. A row is its
 * text plus the dialog's `py-3`: one 20px line, or 20px + a 16px subtitle.
 * Group headings scroll along with the rows.
 */
const VISIBLE_ROWS = 5
const ROW_HEIGHT = 44
const ROW_HEIGHT_WITH_SUBTITLE = 60
/** i18n key under `commandPalette` for each picker's own strings. */
const PICKER_KEYS: Record<PickerMode, string> = {
  networks: 'network',
  pages: 'page',
  reports: 'report',
}

const MAX_RESULTS = 10
const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

interface CommandPaletteProps {
  actions?: CommandAction[]
}

/**
 * Hidden command palette, opened with cmd+shift+p / ctrl+shift+p.
 * Mount once, high up in the tree; it is global for the whole app.
 */
export function CommandPalette({ actions }: CommandPaletteProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { networks, currentNetwork, switchNetwork } = useNetwork()
  const { currentModule, modules } = useModule()
  // Saved quick reports are kept per module side; the palette offers both.
  const advertisingReports = useSavedQueries('advertising')
  const publishingReports = useSavedQueries('publishing')
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<PaletteMode | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey
      if (modifier && event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault()
        handleOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  const defaultActions = useCommandActions()

  const visibleActions = useMemo(() => {
    if (mode) return []
    return (actions ?? defaultActions)
      .map((action) => ({
        action,
        score: fuzzyScore(`${action.label} ${action.keywords ?? ''}`.trim(), query),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.action)
  }, [actions, defaultActions, mode, query])

  /** The visible actions under their headings, best match first within each. */
  const groupedActions = useMemo(
    () =>
      ACTION_GROUPS.map((group) => ({
        group,
        items: visibleActions.filter((action) => action.group === group),
      })).filter((entry) => entry.items.length > 0),
    [visibleActions],
  )

  const picking: PickerMode | null =
    mode && (PICKER_MODES as readonly string[]).includes(mode) ? (mode as PickerMode) : null
  const config = mode && !picking ? SEARCH_MODES[mode as Exclude<PaletteMode, PickerMode>] : null

  /**
   * Networks are already in memory, so they filter locally — no request, and
   * no minimum query length.
   */
  const networkOptions = useMemo(() => {
    if (picking !== 'networks') return []
    return networks
      .filter((network) => network.tagId !== currentNetwork?.tagId)
      .map((network) => ({
        network,
        score: fuzzyScore(`${network.name} ${network.tagId}`, query),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.network)
  }, [picking, networks, currentNetwork, query])

  /**
   * Every page in the current module's sidebar, flattened out of its nav
   * sections and tagged with the section it sits under.
   */
  const pageOptions = useMemo(() => {
    if (picking !== 'pages') return []
    return modules
      .flatMap((module) =>
        module.nav.flatMap((section) =>
          section.items
            .filter((item) => !!item.href)
            .map((item) => ({
              item,
              module,
              section: section.title ?? '',
              current: module.id === currentModule.id,
            })),
        ),
      )
      .map((entry) => ({
        ...entry,
        // The module's name matches too, so "publishing sites" finds it.
        score: fuzzyScore(
          `${entry.item.label} ${entry.section} ${entry.module.label}`.trim(),
          query,
        ),
      }))
      .filter((entry) => entry.score > 0)
      // Best match first, and the module already open wins a tie — with no
      // query every page scores the same, so its pages head the list.
      .sort((a, b) => b.score - a.score || Number(b.current) - Number(a.current))
  }, [picking, modules, currentModule, query])

  /** The reports page the "New report" row opens, and the module's own side. */
  const reportSide = currentModule.id === 'publishing' ? 'publishing' : 'advertising'

  /**
   * The user's saved quick reports, the current module's side first. These
   * live in the user profile and are already loaded, so they filter locally.
   */
  const reportOptions = useMemo(() => {
    if (picking !== 'reports') return []
    const sides: Array<{ side: 'advertising' | 'publishing'; queries: SavedQuery[] }> =
      reportSide === 'publishing'
        ? [
            { side: 'publishing', queries: publishingReports.saved },
            { side: 'advertising', queries: advertisingReports.saved },
          ]
        : [
            { side: 'advertising', queries: advertisingReports.saved },
            { side: 'publishing', queries: publishingReports.saved },
          ]
    return sides
      .flatMap(({ side, queries }) => queries.map((query) => ({ side, query })))
      .map((entry) => ({ ...entry, score: fuzzyScore(entry.query.name, query) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
  }, [picking, advertisingReports.saved, publishingReports.saved, reportSide, query])

  // Debounced so typing does not fire a request per keystroke.
  const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS)
  const searchEnabled =
    open && !!config && AuthService.isAuthenticated() && debouncedQuery.length >= MIN_QUERY_LENGTH

  const { data, isFetching } = useQuery({
    queryKey: ['commandPaletteSearch', config?.type, debouncedQuery],
    queryFn: () =>
      SearchApi.search({ q: debouncedQuery, types: config!.type, pageSize: MAX_RESULTS }),
    enabled: searchEnabled,
    staleTime: 30_000,
  })

  const results = useMemo(
    () => (data?.searchResults ?? []).filter((r) => r.type === config?.type).slice(0, MAX_RESULTS),
    [data, config],
  )

  const subtitleIds = useMemo(
    () => (config?.loadSubtitles ? results.map((r) => r.id) : []),
    [config, results],
  )

  const { data: subtitles, isPending: subtitlesPending } = useQuery({
    queryKey: ['commandPaletteSubtitles', config?.type, subtitleIds],
    queryFn: () => config!.loadSubtitles!(subtitleIds),
    enabled: subtitleIds.length > 0,
    staleTime: 30_000,
  })

  const reset = () => {
    setMode(null)
    setQuery('')
  }

  /**
   * The palette is emptied on the way in, not on the way out: clearing it while
   * the dialog is still animating closed shows a flash of the action list
   * before whatever was picked takes effect.
   */
  const handleOpenChange = (next: boolean) => {
    if (next) reset()
    setOpen(next)
  }

  const runAction = (action: CommandAction) => {
    if (action.mode) {
      // Hand the text field over to the mode's own search.
      setMode(action.mode)
      setQuery('')
      return
    }
    handleOpenChange(false)
    action.run?.()
  }

  const pickNetwork = (tagId: string) => {
    handleOpenChange(false)
    switchNetwork(tagId)
  }

  const openPage = (href: string) => {
    handleOpenChange(false)
    navigate(href)
  }

  const openReport = (path: string) => {
    handleOpenChange(false)
    navigate(path)
  }

  const openResult = (id: string) => {
    const path = config!.path(id)
    handleOpenChange(false)
    navigate(path)
  }

  // Escape backs out of a mode first, and only then closes the palette.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && mode) {
      event.preventDefault()
      reset()
    }
  }

  const ResultIcon = config?.icon ?? Search

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton={false}
      shouldFilter={false}
      // Anchored near the top of the viewport, like the VS Code palette.
      className="top-[10vh] translate-y-0"
      title={t('commandPalette.title')}
      description={t('commandPalette.description')}
    >
      {/* Filtering is done here so the lists can be capped. */}
      <CommandInput
        value={query}
        onValueChange={setQuery}
        onKeyDown={handleKeyDown}
        placeholder={
          config
            ? t(`commandPalette.${config.key}.placeholder`)
            : picking
              ? t(`commandPalette.${PICKER_KEYS[picking]}.placeholder`)
              : t('commandPalette.placeholder')
        }
      />
      <CommandList
        style={{
          maxHeight:
            VISIBLE_ROWS * (config?.loadSubtitles ? ROW_HEIGHT_WITH_SUBTITLE : ROW_HEIGHT),
        }}
      >
        {!mode && (
          <>
            <CommandEmpty>{t('commandPalette.empty')}</CommandEmpty>
            {groupedActions.map(({ group, items }) => (
              <CommandGroup key={group} heading={t(`commandPalette.groups.${group}`)}>
                {items.map((action) => (
                  <CommandItem key={action.id} value={action.id} onSelect={() => runAction(action)}>
                    {action.icon}
                    {action.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </>
        )}

        {picking === 'networks' && (
          <CommandGroup heading={t('commandPalette.network.heading')}>
            {networkOptions.map((network) => (
              <CommandItem
                key={network.tagId}
                value={network.tagId}
                onSelect={() => pickNetwork(network.tagId)}
              >
                <Network className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{network.name}</span>
              </CommandItem>
            ))}

            {networkOptions.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('commandPalette.network.empty')}
              </div>
            )}
          </CommandGroup>
        )}

        {picking === 'pages' && (
          <CommandGroup heading={t('commandPalette.page.heading')}>
            {pageOptions.map(({ item, module, current }) => (
              <CommandItem
                // The same page id can appear in more than one module.
                key={`${module.id}-${item.id}`}
                value={`${module.id}-${item.id}`}
                onSelect={() => openPage(item.href!)}
              >
                <Icon name={item.icon} className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {!current && (
                  <span className="shrink-0 text-xs text-muted-foreground">{module.label}</span>
                )}
              </CommandItem>
            ))}

            {pageOptions.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('commandPalette.page.empty')}
              </div>
            )}
          </CommandGroup>
        )}

        {picking === 'reports' && (
          <CommandGroup heading={t('commandPalette.report.heading')}>
            {/* Always first, and never filtered out — it is the way to start
                a report that has not been saved. */}
            <CommandItem
              value="new-report"
              onSelect={() => openReport(`/${reportSide}/reports`)}
            >
              <Plus className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">{t('commandPalette.report.new')}</span>
            </CommandItem>

            {reportOptions.map(({ side, query: saved }) => (
              <CommandItem
                key={saved.id}
                value={saved.id}
                onSelect={() => openReport(`/${side}/reports?saved=${saved.id}`)}
              >
                <BarChart3 className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{saved.name}</span>
                {side !== reportSide && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t(`commandPalette.report.sides.${side}`)}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {config && (
          <CommandGroup heading={t(`commandPalette.${config.key}.heading`)}>
            {results.map((result) => (
              <CommandItem
                key={result.id}
                value={result.id}
                onSelect={() => openResult(result.id)}
              >
                <ResultIcon className="shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{result.name}</span>
                  {/* The row is sized for the second line from the start, so
                      rows do not grow once the subtitles arrive. */}
                  {config.loadSubtitles && (
                    <span className="flex h-4 items-center text-xs text-muted-foreground">
                      {subtitlesPending ? (
                        <Skeleton className="h-3 w-32" />
                      ) : (
                        <span className="truncate">{subtitles?.[result.id] ?? ''}</span>
                      )}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}

            {results.length === 0 && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                {isFetching && <Loader2 className="size-4 animate-spin" />}
                {isFetching
                  ? t('commandPalette.searching')
                  : debouncedQuery.length >= MIN_QUERY_LENGTH
                    ? t(`commandPalette.${config.key}.empty`)
                    : t('commandPalette.hint')}
              </div>
            )}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
